import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiDelete, apiGet, apiUpload } from '../../api/client';
import { MediaHealthCard } from '../../components/admin/SiteSafetyPanel';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'used', label: 'In use' },
  { id: 'unused', label: 'Unused' },
];

export default function AdminMedia() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [unused, setUnused] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, health] = await Promise.all([
        apiGet('/admin/media?limit=500'),
        apiGet('/admin/media-health').catch(() => null),
      ]);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setUnused(health ? new Set(health.unused || []) : null);
    } catch (err) {
      setMessage(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const uploadFiles = async (files) => {
    const images = files.filter((f) => f.type.startsWith('image/'));
    if (!images.length) return;
    setUploading(true);
    setMessage('');
    try {
      for (const file of images) {
        const formData = new FormData();
        formData.append('file', file);
        await apiUpload('/admin/media', formData);
      }
      setMessage(`Uploaded ${images.length} image${images.length === 1 ? '' : 's'}.`);
      await load();
    } catch (err) {
      setMessage(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async (e) => {
    await uploadFiles(Array.from(e.target.files || []));
    e.target.value = '';
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.originalName}" from the server? This cannot be undone.`)) return;
    setMessage('');
    try {
      await apiDelete(`/admin/media/${item.id}`);
      setMessage('Image deleted.');
      await load();
    } catch (err) {
      setMessage(`${err.message}. Replace it there first, then delete.`);
    }
  };

  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setMessage('URL copied to clipboard.');
    } catch {
      setMessage(url);
    }
  };

  const isUnused = (item) => unused?.has(item.filename || item.url?.split('/').pop());

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((item) => {
      if (q && !`${item.originalName} ${item.url} ${item.alt || ''}`.toLowerCase().includes(q)) return false;
      if (filter === 'used' && (unused == null || isUnused(item))) return false;
      if (filter === 'unused' && (unused == null || !isUnused(item))) return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, search, filter, unused]);

  return (
    <div
      className={`admin-panel-page admin-media-page ${dragging ? 'is-dragging' : ''}`}
      onDragOver={(e) => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); setDragging(true); } }}
      onDragLeave={(e) => { if (e.currentTarget === e.target) setDragging(false); }}
      onDrop={(e) => {
        if (!e.dataTransfer.files?.length) return;
        e.preventDefault();
        setDragging(false);
        uploadFiles(Array.from(e.dataTransfer.files));
      }}
    >
      <div className="admin-panel-toolbar">
        <div className="admin-panel-toolbar-row">
          <div className="admin-stat-chips">
            <div className="admin-stat-chip">
              <span className="admin-stat-value">{total}</span>
              <span className="admin-stat-label">Files</span>
            </div>
            {unused && (
              <div className="admin-stat-chip">
                <span className="admin-stat-value">{items.filter(isUnused).length}</span>
                <span className="admin-stat-label">Unused</span>
              </div>
            )}
          </div>
          <div className="admin-panel-toolbar-actions">
            <button type="button" className="btn btn-primary" onClick={() => fileRef.current?.click()} disabled={uploading}>
              <i className="fas fa-upload" aria-hidden="true" /> {uploading ? 'Uploading…' : 'Upload images'}
            </button>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple hidden onChange={handleUpload} />
          </div>
        </div>
        <div className="admin-filter-row">
          <div className="admin-search">
            <i className="fas fa-search" aria-hidden="true" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search file names…" />
          </div>
          <div className="admin-panel-tabs">
            {FILTERS.map((f) => (
              <button key={f.id} type="button" className={`admin-panel-tab ${filter === f.id ? 'active' : ''}`} onClick={() => setFilter(f.id)}>
                {f.label}
              </button>
            ))}
          </div>
          <span className="admin-filter-hint">Drop images anywhere on this page to upload · JPEG, PNG, WebP, GIF up to 5 MB</span>
        </div>
      </div>

      {message && <p className="admin-message">{message}</p>}

      <MediaHealthCard />

      <div className="admin-settings-card admin-settings-card--wide">
        {loading ? (
          <p>Loading…</p>
        ) : visible.length === 0 ? (
          <p>{items.length ? 'No images match.' : 'No uploads yet.'}</p>
        ) : (
          <div className="admin-media-grid">
            {visible.map((item) => (
              <div key={item.id} className="admin-media-grid__item">
                <img src={item.url} alt={item.originalName} loading="lazy" />
                {unused && (
                  <span className={`admin-pill admin-media-grid__badge ${isUnused(item) ? 'is-muted' : 'is-green'}`}>
                    {isUnused(item) ? 'Unused' : 'In use'}
                  </span>
                )}
                <div className="admin-media-grid__meta">
                  <span title={item.originalName}>{item.originalName}</span>
                  <code>{item.url}</code>
                </div>
                <div className="admin-media-grid__actions">
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => copyUrl(item.url)}>
                    Copy URL
                  </button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => handleDelete(item)}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
